import React from 'react';

/**
 * Store Badge System for AllTuc Marketplace
 * MercadoLibre-style badges for store identification and trust
 */

export type StoreType = 'official' | 'verified' | 'basic';
export type StoreBadgeType = 
  | 'type'           // Tienda Oficial, Verificada, etc.
  | 'new'            // Nuevo en la tienda
  | 'fastShipping'   // Envío Rápido
  | 'topRated'       // Mejor Vendido
  | 'quickResponse'  // Respuesta Rápida
  | 'fiveStars'      // 5 Estrellas
  | 'trust'          // Verificado / Confiable
  | 'guarantee'      // Garantía AllTuc
  | 'freeReturns'    // Devolución Gratuita
  | 'activatable';   // Marca para auto-aprobar en el futuro

interface StoreBadgeProps {
  type: StoreBadgeType;
  storeType?: StoreType;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
  className?: string;
}

interface StoreBadgeConfig {
  label: string;
  icon: string;
  bgColor: string;
  textColor: string;
  borderColor?: string;
}

const badgeConfigs: Record<StoreType | Exclude<StoreBadgeType, 'type'>, StoreBadgeConfig> = {
  // Store Types
  official: {
    label: 'Tienda Oficial',
    icon: 'verified',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-600',
    borderColor: 'border-blue-200'
  },
  verified: {
    label: 'Vendedor Verificado',
    icon: 'check_circle',
    bgColor: 'bg-green-50',
    textColor: 'text-green-600',
    borderColor: 'border-green-200'
  },
  basic: {
    label: 'Vendedor',
    icon: 'store',
    bgColor: 'bg-gray-50',
    textColor: 'text-gray-600',
    borderColor: 'border-gray-200'
  },
  // Performance Badges
  new: {
    label: 'Nuevo',
    icon: 'new_releases',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-600'
  },
  fastShipping: {
    label: 'Envío Rápido',
    icon: 'local_shipping',
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-600'
  },
  topRated: {
    label: 'Mejor Vendido',
    icon: 'trending_up',
    bgColor: 'bg-red-50',
    textColor: 'text-red-600'
  },
  quickResponse: {
    label: 'Respuesta Rápida',
    icon: 'schedule',
    bgColor: 'bg-teal-50',
    textColor: 'text-teal-600'
  },
  fiveStars: {
    label: '5 Estrellas',
    icon: 'stars',
    bgColor: 'bg-yellow-50',
    textColor: 'text-yellow-600'
  },
  // Trust Badges
  trust: {
    label: 'Verificado',
    icon: 'verified_user',
    bgColor: 'bg-emerald-50',
    textColor: 'text-emerald-600'
  },
  guarantee: {
    label: 'Garantía AllTuc',
    icon: 'shield',
    bgColor: 'bg-indigo-50',
    textColor: 'text-indigo-600'
  },
  freeReturns: {
    label: 'Devolución Gratuita',
    icon: 'replay',
    bgColor: 'bg-pink-50',
    textColor: 'text-pink-600'
  },
  // Admin Features
  activatable: {
    label: 'Auto-Aprobar',
    icon: 'auto_awesome',
    bgColor: 'bg-cyan-50',
    textColor: 'text-cyan-600'
  }
};

const sizeClasses = {
  sm: {
    badge: 'px-2 py-0.5 text-[10px]',
    icon: 'text-xs'
  },
  md: {
    badge: 'px-2.5 py-1 text-xs',
    icon: 'text-sm'
  },
  lg: {
    badge: 'px-3 py-1.5 text-sm',
    icon: 'text-base'
  }
};

export const StoreBadge: React.FC<StoreBadgeProps> = ({
  type,
  storeType = 'basic',
  size = 'sm',
  showIcon = true,
  className = ''
}) => {
  const config = type === 'type' ? badgeConfigs[storeType] : badgeConfigs[type];
  const sizeClass = sizeClasses[size];

  return (
    <span
      className={`
        inline-flex items-center gap-1 rounded-full font-bold
        ${config.bgColor}
        ${config.textColor}
        ${config.borderColor ? `border ${config.borderColor}` : ''}
        ${sizeClass.badge}
        ${className}
      `}
    >
      {showIcon && (
        <span className={`material-symbols-outlined ${sizeClass.icon} filled`}>
          {config.icon}
        </span>
      )}
      <span>{config.label}</span>
    </span>
  );
};

/**
 * Store Type Badge - Main badge showing store classification
 */
interface StoreTypeBadgeProps {
  storeType: StoreType;
  isNew?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const StoreTypeBadge: React.FC<StoreTypeBadgeProps> = ({
  storeType,
  isNew = false,
  size = 'md',
  className = ''
}) => {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <StoreBadge type="type" storeType={storeType} size={size} />
      {isNew && <StoreBadge type="new" size={size} />}
    </div>
  );
};

/**
 * Store Performance Badges - Shows store metrics
 */
interface StorePerformanceBadgesProps {
  hasFastShipping?: boolean;
  isTopRated?: boolean;
  hasQuickResponse?: boolean;
  isFiveStars?: boolean;
  maxBadges?: number;
  size?: 'sm' | 'md';
  className?: string;
}

export const StorePerformanceBadges: React.FC<StorePerformanceBadgesProps> = ({
  hasFastShipping,
  isTopRated,
  hasQuickResponse,
  isFiveStars,
  maxBadges = 3,
  size = 'sm',
  className = ''
}) => {
  const badges: StoreBadgeType[] = [];
  
  if (isTopRated) badges.push('topRated');
  if (isFiveStars) badges.push('fiveStars');
  if (hasFastShipping) badges.push('fastShipping');
  if (hasQuickResponse) badges.push('quickResponse');
  
  const displayBadges = badges.slice(0, maxBadges);

  if (displayBadges.length === 0) return null;

  return (
    <div className={`flex flex-wrap gap-1.5 ${className}`}>
      {displayBadges.map((badge) => (
        <StoreBadge key={badge} type={badge} size={size} />
      ))}
    </div>
  );
};

/**
 * Store Trust Badges - Shows trust indicators
 */
interface StoreTrustBadgesProps {
  isVerified?: boolean;
  hasGuarantee?: boolean;
  hasFreeReturns?: boolean;
  maxBadges?: number;
  size?: 'sm' | 'md';
  className?: string;
}

export const StoreTrustBadges: React.FC<StoreTrustBadgesProps> = ({
  isVerified,
  hasGuarantee,
  hasFreeReturns,
  maxBadges = 2,
  size = 'sm',
  className = ''
}) => {
  const badges: StoreBadgeType[] = [];
  
  if (isVerified) badges.push('trust');
  if (hasGuarantee) badges.push('guarantee');
  if (hasFreeReturns) badges.push('freeReturns');
  
  const displayBadges = badges.slice(0, maxBadges);

  if (displayBadges.length === 0) return null;

  return (
    <div className={`flex flex-wrap gap-1.5 ${className}`}>
      {displayBadges.map((badge) => (
        <StoreBadge key={badge} type={badge} size={size} />
      ))}
    </div>
  );
};

/**
 * Store Admin Badge - For admin/moderation purposes
 */
interface StoreAdminBadgeProps {
  isActivatable?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

export const StoreAdminBadge: React.FC<StoreAdminBadgeProps> = ({
  isActivatable,
  size = 'sm',
  className = ''
}) => {
  if (!isActivatable) return null;

  return (
    <StoreBadge 
      type="activatable" 
      size={size} 
      className={className}
      showIcon={true}
    />
  );
};

/**
 * Complete Store Badge Group - Combines all relevant badges
 */
interface StoreBadgeGroupProps {
  storeType: StoreType;
  isNew?: boolean;
  isVerified?: boolean;
  hasFastShipping?: boolean;
  isTopRated?: boolean;
  hasGuarantee?: boolean;
  isActivatable?: boolean;
  showPerformance?: boolean;
  showTrust?: boolean;
  showAdmin?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const StoreBadgeGroup: React.FC<StoreBadgeGroupProps> = ({
  storeType,
  isNew,
  isVerified,
  hasFastShipping,
  isTopRated,
  hasGuarantee,
  isActivatable,
  showPerformance = true,
  showTrust = true,
  showAdmin = false,
  size = 'md',
  className = ''
}) => {
  const isSmall = size === 'sm';
  const badgeSize = isSmall ? 'sm' : 'md';

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      {/* Primary Badge: Store Type */}
      <StoreTypeBadge storeType={storeType} isNew={isNew} size={size} />
      
      {/* Performance Badges */}
      {showPerformance && (
        <StorePerformanceBadges
          hasFastShipping={hasFastShipping}
          isTopRated={isTopRated}
          size={badgeSize}
          maxBadges={isSmall ? 1 : 2}
        />
      )}
      
      {/* Trust Badges */}
      {showTrust && (
        <StoreTrustBadges
          isVerified={isVerified}
          hasGuarantee={hasGuarantee}
          size={badgeSize}
          maxBadges={isSmall ? 1 : 2}
        />
      )}
      
      {/* Admin Badge */}
      {showAdmin && isActivatable && (
        <StoreAdminBadge size={badgeSize} />
      )}
    </div>
  );
};

export default StoreBadge;
